import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import { getIntl } from '../../../../../utils/intl';
import ContactDetailsForm from '../../../../shared/ContactDetailsForm';
import { usePromptButton } from '../../../../shared/PromptButton';
import { EmergencyContactDetails } from '../../../../../primary-record-types/apiTypes/medicalProfileService';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';

const intl = getIntl('emergencyContactEdit', {
    submit: 'Save & Exit',
    cancel: 'Cancel',
    deletePrompt: 'Delete Emergency Contact',
    deleteNotificationHeader: 'Are you sure you want to delete this emergency contact?',
    deleteSubtext:
        'This will permanently delete this emergency contact from your profile. You can always add them back later.',
    deleteButton: 'Yes, delete',
    cancelButton: 'No, cancel',
    deleteActionText: 'Delete',
    editEmergencyContact: 'Edit Emergency Contact',
});

export type EmergencyContactEditScreenOwnProps = {};

export type EmergencyContactEditScreenProps = EmergencyContactEditScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'edit-emergency-contact'>;
};

const EmergencyContactEditScreen: React.FC<EmergencyContactEditScreenProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const profileNavigation = useProfilesTabNavigation();
    const handleNavigateToProfile = () => {
        if (isMobile) {
            profileNavigation.navigate('profile', {
                profileId: props.route.params.profileId,
            });
        } else {
            profileNavigation.navigate('profile-activity', {
                profileId: props.route.params.profileId,
            });
        }
    };
    const {
        isLoadingProfile,
        profile,
        updateContact,
        isUpdatingContact,
        isDeletingEmergencyContact,
        deleteEmergencyContact,
    } = useMedicalProfile({
        medicalProfileId: props.route.params.profileId,
        onContactDetailsUpdateSuccess: handleNavigateToProfile,
        onDeleteEmergencyContactSuccess: handleNavigateToProfile,
    });

    const deletePrompt = usePromptButton({
        promptTitle: intl('deletePrompt'),
        renderPromptContents: (onClose) => (
            <MainContentContainer>
                <ContentStack>
                    <Text variant="body-1">{intl('deleteSubtext')}</Text>
                    <PromptActions
                        actions={[
                            {
                                title: intl('deleteButton'),
                                onPress: () => {
                                    deleteEmergencyContact({
                                        emergencyContactId: props.route.params.emergencyContactId,
                                        medicalProfileId: props.route.params.profileId,
                                    });
                                    onClose();
                                },
                                variant: 'alert',
                                icon: 'Trash',
                            },
                            {
                                title: intl('cancelButton'),
                                onPress: onClose,
                                variant: 'secondary',
                            },
                        ]}
                    />
                </ContentStack>
            </MainContentContainer>
        ),
    });

    const targetContact = profile?.emergencyContactDetails.find(
        (_) => _.contactDetailsId === props.route.params.emergencyContactId,
    );

    return (
        <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder omitChatButton>
            <AnimateLoadedContent
                isLoading={isUpdatingContact || isDeletingEmergencyContact || isLoadingProfile || !targetContact}
            >
                {() => (
                    <ContactDetailsForm
                        title={intl('editEmergencyContact')}
                        key={JSON.stringify(targetContact)}
                        isDisabled={isUpdatingContact || isDeletingEmergencyContact}
                        isSubmitting={isUpdatingContact}
                        initialValues={targetContact}
                        fields={['firstName', 'lastName', 'relationship', 'email', 'phoneNumber']}
                        required={[]}
                        submitButtonTitle={intl('submit')}
                        onSubmit={(values) => {
                            updateContact({
                                contactDetails: {
                                    contactDetails: values as EmergencyContactDetails,
                                },
                                contactDetailsId: values.contactDetailsId,
                            });
                        }}
                        nonDirtyActions={[
                            {
                                title: intl('deleteActionText'),
                                onPress: deletePrompt.onPress,
                                icon: 'Trash2',
                            },
                        ]}
                    />
                )}
            </AnimateLoadedContent>
            {deletePrompt.Prompt}
        </ProfileHeaderContainer>
    );
};

export default React.memo(EmergencyContactEditScreen);
